import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';

import { Observable } from 'rxjs/Observable';

import { AlertsService, AlertRow } from 'app/alerts/alerts.service';
import { AlertDetailDrawerComponent } from 'app/alerts/alert-detail-drawer.component';
import { SnackBarService } from 'app/services/snackbar.service';
import { AuthenticationGuard } from 'app/services/authentication-guard.service';

/**
 * One entity's worth of open alerts. The inbox groups the flat `AlertRow` feed by
 * `entityId` so a person/org that matches several lists, aliases or products shows
 * as a single expandable row. Triage stays per-hit: expanding reveals the individual
 * {@link AlertRow}s, each of which opens the detail drawer for the Phase-1/2 workflow.
 */
export interface EntityAlertGroup {
  entityId: number;
  entityName: string;
  screeningEntityId?: string;
  hits: AlertRow[];
  sanctionCount: number;
  pepCount: number;
  negativeNewsCount: number;
  leakPapersCount: number;
  totalCount: number;
  worstStatus: string;
  latestHitAt: number;
  sources: string[];
}

/**
 * Single inbox of all open hits across products, grouped by entity. Each entity row
 * expands to its individual hits; opening a hit launches a detail drawer that wraps the
 * existing Phase-1 / Phase-2 case endpoints so a triage decision here is the same as one
 * made in the per-entity Monitoring tab.
 */
@Component({
  selector: 'alerts-inbox',
  templateUrl: './alerts-inbox.component.html',
  styleUrls: ['./alerts-inbox.component.css']
})
export class AlertsInboxComponent implements OnInit {
  alerts: AlertRow[] = [];
  filtered: AlertRow[] = [];
  groups: EntityAlertGroup[] = [];
  private expandedEntityIds: { [id: number]: boolean } = {};

  loading = false;
  total = 0;
  deletingEntityIds: { [id: number]: boolean } = {};

  filter = {
    text: '',
    product: 'all' as 'all' | 'sanctions' | 'pep' | 'negative_news' | 'leak_papers',
    status: 'OPEN' as 'OPEN' | 'ALL',
    source: 'all',
    accountManager: 'all',
    fromDate: '' as string,
    toDate: '' as string,
  };

  availableSources: string[] = [];
  availableAccountManagers: string[] = [];

  dialogRef: MdDialogRef<any>;

  constructor(
    public dialog: MdDialog,
    public viewContainerRef: ViewContainerRef,
    private alertsService: AlertsService,
    private snackBarService: SnackBarService,
    private authenticationGuard: AuthenticationGuard
  ) { }

  ngOnInit() {
    this.refresh();
  }

  refresh() {
    this.loading = true;
    const products = this.filter.product === 'all' ? null : [this.filter.product];
    this.alertsService.listAlerts(products, this.filter.status).subscribe(
      response => {
        this.loading = false;
        const arr = response && response.arrResult;
        this.alerts = (arr && arr.alerts) || [];
        this.total = (arr && arr.total) || this.alerts.length;
        this.computeFacetOptions();
        this.applyFilter();
      },
      err => {
        this.loading = false;
        this.snackBarService.showSimpleMsj((err && err.message) || 'Failed to load alerts.');
      }
    );
  }

  private computeFacetOptions() {
    const sources = new Set<string>();
    const ams = new Set<string>();
    for (const a of this.alerts) {
      if (a.listSource) { sources.add(a.listSource); }
      // Account-manager scoping is enforced server-side; we still expose any AM
      // id propagated through the payload for an additional UI filter.
      const amId = (a as any).accountManagerName || (a as any).accountManager || null;
      if (amId) { ams.add(amId); }
    }
    this.availableSources = Array.from(sources).sort();
    this.availableAccountManagers = Array.from(ams).sort();
  }

  applyFilter() {
    const q = (this.filter.text || '').toLowerCase().trim();
    const fromMs = this.filter.fromDate ? new Date(this.filter.fromDate).getTime() : null;
    const toMs = this.filter.toDate ? new Date(this.filter.toDate).getTime() + (24 * 3600 * 1000 - 1) : null;
    this.filtered = this.alerts.filter(a => {
      if (this.filter.product !== 'all' && a.product !== this.filter.product) { return false; }
      if (this.filter.source !== 'all' && a.listSource !== this.filter.source) { return false; }
      if (this.filter.accountManager !== 'all') {
        const amId = (a as any).accountManagerName || (a as any).accountManager;
        if (amId !== this.filter.accountManager) { return false; }
      }
      if (fromMs != null && (!a.hitDetectedAt || a.hitDetectedAt < fromMs)) { return false; }
      if (toMs != null && (!a.hitDetectedAt || a.hitDetectedAt > toMs)) { return false; }
      if (q) {
        const hay = [a.entityName, a.summary, a.listSource, a.product, this.hitStatusLabel(a)]
          .filter(Boolean).join(' ').toLowerCase();
        if (hay.indexOf(q) === -1) { return false; }
      }
      return true;
    });
    this.buildGroups();
  }

  /** Collapse the filtered hits into one expandable row per entity. */
  private buildGroups() {
    const map: { [id: number]: EntityAlertGroup } = {};
    const order: number[] = [];
    for (const a of this.filtered) {
      let g = map[a.entityId];
      if (!g) {
        g = {
          entityId: a.entityId,
          entityName: a.entityName,
          screeningEntityId: a.screeningEntityId,
          hits: [],
          sanctionCount: 0,
          pepCount: 0,
          negativeNewsCount: 0,
          leakPapersCount: 0,
          totalCount: 0,
          worstStatus: 'RESOLVED',
          latestHitAt: 0,
          sources: []
        };
        map[a.entityId] = g;
        order.push(a.entityId);
      }
      g.hits.push(a);
      g.totalCount++;
      if (a.product === 'sanctions') { g.sanctionCount++; }
      else if (a.product === 'pep') { g.pepCount++; }
      else if (a.product === 'negative_news') { g.negativeNewsCount++; }
      else if (a.product === 'leak_papers') { g.leakPapersCount++; }
      if (a.hitDetectedAt && a.hitDetectedAt > g.latestHitAt) { g.latestHitAt = a.hitDetectedAt; }
      if (a.listSource && g.sources.indexOf(a.listSource) === -1) { g.sources.push(a.listSource); }
      g.worstStatus = this.moreUrgentStatus(g.worstStatus, this.hitStatusKey(a));
    }
    const groups = order.map(id => map[id]);
    groups.sort((x, y) => {
      const sx = this.statusUrgency(x.worstStatus);
      const sy = this.statusUrgency(y.worstStatus);
      if (sx !== sy) { return sx - sy; }
      return (y.latestHitAt || 0) - (x.latestHitAt || 0);
    });
    for (const g of groups) {
      g.hits.sort((h1, h2) => (h2.hitDetectedAt || 0) - (h1.hitDetectedAt || 0));
      g.sources.sort();
    }
    this.groups = groups;
  }

  /** Lower number = more urgent; drives group ordering. */
  private statusUrgency(s: string): number {
    if (s === 'OPEN') { return 0; }
    if (s === 'CONFIRMED') { return 1; }
    return 2;
  }

  private hitStatusKey(h: AlertRow): string {
    if (h.processed !== true) { return 'OPEN'; }
    if (h.matchResolution === 'True') { return 'CONFIRMED'; }
    return 'RESOLVED';
  }

  hitStatusLabel(h: AlertRow): string {
    const key = this.hitStatusKey(h);
    if (key === 'OPEN') { return 'Open'; }
    if (key === 'CONFIRMED') { return 'Confirmed'; }
    if (h.matchResolution === 'False') { return 'False positive'; }
    return 'Resolved';
  }

  hitStatusClass(h: AlertRow): string {
    const key = this.hitStatusKey(h);
    if (key === 'OPEN') { return 'status-pill status-open'; }
    if (key === 'CONFIRMED') { return 'status-pill status-positive'; }
    if (h.matchResolution === 'False') { return 'status-pill status-negative'; }
    return 'status-pill status-negative';
  }

  toggle(g: EntityAlertGroup) {
    this.expandedEntityIds[g.entityId] = !this.expandedEntityIds[g.entityId];
  }

  isExpanded(g: EntityAlertGroup): boolean {
    return !!this.expandedEntityIds[g.entityId];
  }

  groupStatusLabel(g: EntityAlertGroup): string {
    if (g.worstStatus === 'OPEN') { return 'Open'; }
    if (g.worstStatus === 'CONFIRMED') { return 'Confirmed'; }
    return 'Resolved';
  }

  groupStatusClass(g: EntityAlertGroup): string {
    if (g.worstStatus === 'OPEN') { return 'status-pill status-open'; }
    if (g.worstStatus === 'CONFIRMED') { return 'status-pill status-positive'; }
    return 'status-pill status-negative';
  }

  private moreUrgentStatus(current: string, candidate: string): string {
    return this.statusUrgency(candidate) < this.statusUrgency(current)
      ? (candidate || 'OPEN') : current;
  }

  setFilterText(v: string) {
    this.filter.text = v || '';
    this.applyFilter();
  }

  onProductChange() {
    // Server-side filter changes the candidate set, so re-fetch.
    this.refresh();
  }

  onStatusChange() {
    this.refresh();
  }

  onSourceChange() {
    this.applyFilter();
  }

  onAccountManagerChange() {
    this.applyFilter();
  }

  onDateRangeChange() {
    this.applyFilter();
  }

  productLabel(p: string): string {
    if (p === 'sanctions') { return 'Sanctions'; }
    if (p === 'pep') { return 'PEP'; }
    if (p === 'negative_news') { return 'Negative news'; }
    if (p === 'leak_papers') { return 'Leak papers'; }
    return p;
  }

  productPillClass(p: string): string {
    if (p === 'negative_news') { return 'type-pill type-badpress'; }
    if (p === 'leak_papers') { return 'type-pill type-leak'; }
    return 'type-pill type-' + p;
  }

  open(alert: AlertRow) {
    const config = new MdDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    config.width = '720px';
    this.dialogRef = this.dialog.open(AlertDetailDrawerComponent, config);
    this.dialogRef.componentInstance.alertSummary = alert;
    this.dialogRef.componentInstance.isAdminUser = this.isAdminUser();
    this.dialogRef.afterClosed().subscribe((changed: boolean) => {
      this.dialogRef = null;
      if (changed) {
        this.refresh();
      }
    });
  }

  isAdminUser(): boolean {
    return !!(this.authenticationGuard && this.authenticationGuard.isAdminUser());
  }

  deleteAllHitsForEntity(g: EntityAlertGroup, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isAdminUser() || !g || !g.entityId || this.deletingEntityIds[g.entityId]) {
      return;
    }
    const count = g.totalCount || 0;
    if (count === 0) {
      this.snackBarService.showSimpleMsj('No hits to delete.');
      return;
    }
    const name = g.entityName || ('Entity ' + g.entityId);
    if (!confirm('Delete all monitoring hits (sanctions, PEP, bad press) for ' + name
        + '? This removes open and resolved hits and cannot be undone.')) {
      return;
    }
    this.deletingEntityIds[g.entityId] = true;
    this.alertsService.deleteAllHits(g.entityId).subscribe(
      response => {
        delete this.deletingEntityIds[g.entityId];
        const ok = response && response.flagResult;
        this.snackBarService.showSimpleMsj(
          ok ? 'All hits deleted.' : (response && response.message) || 'Delete failed.');
        if (ok) {
          this.refresh();
        }
      },
      err => {
        delete this.deletingEntityIds[g.entityId];
        this.snackBarService.showSimpleMsj((err && err.message) || 'Failed to delete hits.');
      }
    );
  }

  isDeletingEntity(entityId: number): boolean {
    return !!this.deletingEntityIds[entityId];
  }

  isComplianceOrAdmin(): boolean {
    return this.authenticationGuard
      && (this.authenticationGuard.isAdminUser() || this.isComplianceOfficer());
  }

  private isComplianceOfficer(): boolean {
    const ag: any = this.authenticationGuard;
    return !!(ag && typeof ag.isComplianceOfficer === 'function' && ag.isComplianceOfficer());
  }
}



// WEBPACK FOOTER //
// ./src/app/alerts/alerts-inbox.component.ts